import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Plus, Pencil, Trash2, Check, Tag } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { partnershipsApiService } from '../../services/partnershipsApi';
import type { PartnershipTag } from '../../types/partnership';
interface TagManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DESCRIPTION_TRUNCATE_LENGTH = 40;

function normalizeHexColor(value: string): string {
  const cleaned = value.replace(/^#/, '').trim();
  if (/^[0-9A-Fa-f]{6}$/.test(cleaned)) {
    return `#${cleaned}`;
  }
  if (/^[0-9A-Fa-f]{3}$/.test(cleaned)) {
    return `#${cleaned[0]}${cleaned[0]}${cleaned[1]}${cleaned[1]}${cleaned[2]}${cleaned[2]}`;
  }
  return value.startsWith('#') ? value : `#${value}`;
}

export function TagManagementModal({ isOpen, onClose }: TagManagementModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [tags, setTags] = useState<PartnershipTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [createName, setCreateName] = useState('');
  const [createColor, setCreateColor] = useState('#3b82f6');
  const [createDescription, setCreateDescription] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getAllTags();
      setTags(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!createName.trim()) {
      setError('Name is required');
      return;
    }
    const color = normalizeHexColor(createColor);
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setError('Please enter a valid hex color (e.g. #3b82f6)');
      return;
    }
    setCreateSubmitting(true);
    try {
      const created = await partnershipsApiService.createTag({
        name: createName.trim(),
        color,
        description: createDescription.trim() || undefined,
      });
      setTags((prev) => [...prev, created]);
      setCreateName('');
      setCreateColor('#3b82f6');
      setCreateDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const startEdit = (tag: PartnershipTag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color.startsWith('#') ? tag.color : `#${tag.color}`);
    setEditDescription(tag.description ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
    setEditDescription('');
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    setError(null);
    if (!editName.trim()) {
      setError('Name is required');
      return;
    }
    const color = normalizeHexColor(editColor);
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setError('Please enter a valid hex color (e.g. #3b82f6)');
      return;
    }
    setEditSubmitting(true);
    try {
      const updated = await partnershipsApiService.updateTag(editingId, {
        name: editName.trim(),
        color,
        description: editDescription.trim() || null,
      });
      setTags((prev) => prev.map((t) => (t.id === editingId ? updated : t)));
      cancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tag');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    setDeleteSubmitting(true);
    try {
      await partnershipsApiService.deleteTag(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tag');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-1 touch-manipulation"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          <Tag className="w-5 h-5 text-accent-400" />
          Manage Tags
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        {/* Create new tag form */}
        <form onSubmit={handleCreate} className="space-y-3 mb-6 pb-4 border-b border-glass-200/20">
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Name *</label>
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Tag name"
              required
            />
          </div>
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Color</label>
            <div className="flex items-center gap-2 flex-wrap">
              <div
                className="w-10 h-10 rounded-lg border border-glass-200/30 flex-shrink-0"
                style={{ backgroundColor: createColor.startsWith('#') ? createColor : `#${createColor}` }}
              />
              <input
                type="text"
                value={createColor}
                onChange={(e) => setCreateColor(e.target.value)}
                className="glass-input flex-1 min-w-[100px] text-sm font-mono"
                placeholder="#3b82f6"
              />
              <input
                type="color"
                value={createColor.startsWith('#') ? createColor : `#${createColor}`}
                onChange={(e) => setCreateColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border border-glass-200/30 bg-transparent flex-shrink-0 p-0"
                title="Pick color"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Description</label>
            <input
              type="text"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Optional description"
            />
          </div>
          <button
            type="submit"
            className="glass-button w-full flex items-center justify-center gap-2"
            disabled={createSubmitting}
          >
            {createSubmitting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Add Tag
          </button>
        </form>

        {/* Tags list */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-glass-300 mb-2">Existing Tags</h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 animate-spin text-accent-400" />
            </div>
          ) : tags.length === 0 ? (
            <p className="text-sm text-glass-400 py-4 text-center">No tags yet. Create one above.</p>
          ) : (
            <ul className="space-y-2">
              {tags.map((tag) => (
                <li key={tag.id}>
                  {editingId === tag.id ? (
                    <div className="glass-card p-3 space-y-3 border border-accent-500/30">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="glass-input w-full text-sm"
                        placeholder="Name"
                      />
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded border border-glass-200/30 flex-shrink-0"
                          style={{
                            backgroundColor: editColor.startsWith('#') ? editColor : `#${editColor}`,
                          }}
                        />
                        <input
                          type="text"
                          value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          className="glass-input flex-1 text-sm font-mono"
                          placeholder="#3b82f6"
                        />
                        <input
                          type="color"
                          value={editColor.startsWith('#') ? editColor : `#${editColor}`}
                          onChange={(e) => setEditColor(e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border border-glass-200/30 bg-transparent flex-shrink-0 p-0"
                        />
                      </div>
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="glass-input w-full text-sm"
                        placeholder="Description"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="glass-button-secondary flex-1 py-2 text-sm"
                          disabled={editSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleUpdate}
                          className="glass-button flex-1 py-2 text-sm flex items-center justify-center gap-1"
                          disabled={editSubmitting}
                        >
                          {editSubmitting ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : deleteConfirmId === tag.id ? (
                    <div className="glass-card p-3 flex items-center justify-between gap-3 flex-wrap">
                      <span className="text-sm text-glass-300">Delete &quot;{tag.name}&quot;?</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(null)}
                          className="glass-button-secondary py-1.5 px-3 text-sm"
                          disabled={deleteSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(tag.id)}
                          className="glass-button py-1.5 px-3 text-sm flex items-center gap-1 text-red-300 bg-red-500/15 hover:bg-red-500/30 border-red-400/30"
                          disabled={deleteSubmitting}
                        >
                          {deleteSubmitting ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="glass-card p-3 flex items-center gap-3 flex-wrap">
                      <div
                        className="w-6 h-6 rounded-full flex-shrink-0 border border-glass-200/30"
                        style={{
                          backgroundColor: tag.color.startsWith('#') ? tag.color : `#${tag.color}`,
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-white block truncate">{tag.name}</span>
                        {tag.description && (
                          <span className="text-xs text-glass-400 block truncate" title={tag.description}>
                            {tag.description.length > DESCRIPTION_TRUNCATE_LENGTH
                              ? `${tag.description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}…`
                              : tag.description}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(tag)}
                          className="glass-button-secondary p-2 touch-manipulation"
                          aria-label="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(tag.id)}
                          className="glass-button-secondary p-2 text-red-400 hover:bg-red-500/15 touch-manipulation"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
